import React, { useEffect, useRef, useState } from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import { useDispatch, useSelector } from "react-redux";
import Highlight from "./components/Highlight";
import MeetingAgenda from "./components/MeetingAgenda";
import Notes from "./components/Notes";
import CueCards from "./components/CueCards";
import TalkingRatio from "./components/TalkingRatio";
import LiveAssist from "./components/LiveAssist";
import { useLiveAssist } from "./hooks/useLiveAssist";
import { setMeetingId, setOrganizerId, setJoinUrl } from "./api/meetingSlice";

// ---------------------------------------------------------------------------
// Teams theme helpers
// Teams SDK sends one of three strings:
//   "default"  → Teams Light
//   "dark"     → Teams Dark
//   "contrast" → Teams High Contrast (accessibility)
// We map each to a data-theme attribute on the root element.
// ---------------------------------------------------------------------------
function applyTeamsTheme(theme) {
  console.log("[Theme] Applying theme:", theme);
  document.documentElement.setAttribute("data-theme", theme);
}

// Read theme from either SDK v2 (context.app.theme) or SDK v1 (context.theme)
function extractTheme(context) {
  return context?.app?.theme || context?.theme || "default";
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getMeetingIdFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get("meetingId");
}

function getOrganizerIdFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get("organizerId");
}

function getTeamsMeetingDetails() {
  return new Promise((resolve) => {
    if (!microsoftTeams.meeting?.getMeetingDetails) {
      resolve(null);
      return;
    }
    microsoftTeams.meeting.getMeetingDetails((error, meetingDetails) => {
      if (error) {
        console.log("Error loading Teams meeting details:", error);
        resolve(null);
        return;
      }
      resolve(meetingDetails);
    });
  });
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------
function App() {
  const dispatch = useDispatch();
  const meetingId = useSelector((state) => state.meeting.meetingId);
  const organizerId = useSelector((state) => state.meeting.organizerId);
  const [meetingContext, setMeetingContext] = useState(null);
  const [role, setRole] = useState(null);
  const [roleResolved, setRoleResolved] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [theme, setTheme] = useState("default");

  // Apply theme to DOM whenever it changes
  useEffect(() => {
    applyTeamsTheme(theme);
  }, [theme]);
  // Owns the live-assist session; the Start/Stop control below lives in the
  // header, while <LiveAssist /> in the Assistant tab renders the cue cards.
  const liveAssist = useLiveAssist();

  // ---------------------------------------------------------------------------
  // Teams SDK initialization
  // Step 1: initialize → Step 2: getContext (reads current theme + role) →
  // Step 3: register live theme handler → Step 4: get meeting details
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // In React dev mode with HMR, initialize() might be called twice and throw an error.
    // We catch it and continue so that getContext() still runs.
    microsoftTeams.app
      .initialize()
      .catch((err) => {
        console.warn("Teams initialize warning (often due to HMR):", err);
      })
      .then(() => {
        // CRITICAL: because the manifest sets "showLoadingIndicator": true,
        // Teams shows its own loading spinner until we call notifySuccess().
        // Without this, the tab spins forever and never renders our content.
        microsoftTeams.app.notifySuccess();
        return microsoftTeams.app.getContext();
      })
      .then((context) => {
        console.log("[Teams] context:", context);

        // ── THEME ────────────────────────────────────────────────────────────
        // Support both SDK v2 (context.app.theme) and SDK v1 (context.theme).
        const initialTheme = extractTheme(context);
        setTheme(initialTheme);

        // Register ONCE for live theme-change events.
        microsoftTeams.app.registerOnThemeChangeHandler((newTheme) => {
          setTheme(newTheme || "default");
        });
        console.log("[Theme] Handler registered. Current theme:", initialTheme);
        // ─────────────────────────────────────────────────────────────────────

        setRole(context?.meeting?.role || null);
        setRoleResolved(true);

        return getTeamsMeetingDetails().then((meetingDetails) => ({
          context,
          meetingDetails,
        }));
      })
      .then(({ meetingDetails }) => {
        console.log("Teams meeting details:", meetingDetails);

        const meetingJoinUrl = meetingDetails?.details?.joinUrl;
        const meetingDetailsId = meetingDetails?.details?.id;
        const urlMeetingId = getMeetingIdFromUrl();
        const resolvedOrganizerId =
          meetingDetails?.organizer?.id || getOrganizerIdFromUrl();

        // Intentionally NOT using context.meeting.id — that returns the Teams
        // chat thread id (19:meeting_xxx@thread.v2) which sellerService does
        // not store. Only joinUrl / onlineMeeting.id resolve in the backend.
        const resolvableMeetingId =
          meetingJoinUrl || meetingDetailsId || urlMeetingId;

        if (resolvableMeetingId) {
          dispatch(setMeetingId(resolvableMeetingId));
          setMeetingContext(
            meetingJoinUrl
              ? "joinUrl from microsoftTeams.meeting.getMeetingDetails"
              : meetingDetailsId
              ? "details.id from microsoftTeams.meeting.getMeetingDetails"
              : "meetingId from URL query parameter"
          );
        }

        // Recall.ai needs the real Teams join URL to send its bot in.
        if (meetingJoinUrl) {
          dispatch(setJoinUrl(meetingJoinUrl));
        }

        if (resolvedOrganizerId) {
          console.log(
            "Setting organizer id from Teams meeting details: " +
              resolvedOrganizerId
          );
          dispatch(setOrganizerId(resolvedOrganizerId));
        }
      })
      .catch((err) => {
        console.error("Error getting Teams context/details:", err);
        setTheme("dark"); // fallback to dark if SDK unavailable
        setRoleResolved(true);
        const urlMeetingId = getMeetingIdFromUrl();
        const urlOrganizerId = getOrganizerIdFromUrl();
        if (urlMeetingId) {
          dispatch(setMeetingId(urlMeetingId));
          setMeetingContext(
            "meetingId from URL (Teams SDK init failed): " +
              JSON.stringify(err)
          );
        }
        if (urlOrganizerId) {
          console.log(
            "Setting organizer id from URL due to Teams SDK init failure: " +
              urlOrganizerId
          );
          dispatch(setOrganizerId(urlOrganizerId));
        }
      });
  }, [dispatch]); // dispatch is stable

  // Allow when role is unknown (e.g. opened directly via teams.salfri.com
  // outside a Teams meeting) so existing browser access keeps working.
  const canEdit = !roleResolved || role == null || role === "Organizer";

  return (
    <div className="app-container">
      <div className="glass-panel">
        <header className="app-header">
          <div className="brand-section">
            <img
              src="/logo-small.png"
              alt="Salfri Logo"
              className="logo-image"
            />
            <div>
              <h1 className="gradient-title">Salfri Assistant</h1>
              <p className="subtitle">Digital Meeting Intelligence</p>
            </div>
          </div>

          {canEdit && (
            <div className="header-actions">
              {liveAssist.started && (
                <span
                  className="updating-badge"
                  style={{ color: "var(--success)", background: "var(--success-glow)" }}
                >
                  <span
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      background: "var(--success)",
                      display: "inline-block",
                      marginRight: "4px",
                    }}
                  />
                  Live
                </span>
              )}
              {!liveAssist.started ? (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={liveAssist.handleStart}
                  disabled={!liveAssist.canStart || liveAssist.starting}
                  title={
                    liveAssist.canStart
                      ? "Send the assistant bot into this meeting"
                      : "No Teams join link available for this meeting yet"
                  }
                >
                  {liveAssist.starting ? "Starting…" : "Start live assist"}
                </button>
              ) : (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={liveAssist.handleStop}
                  disabled={liveAssist.stopping}
                >
                  {liveAssist.stopping ? "Stopping…" : "Stop live assist"}
                </button>
              )}
            </div>
          )}
        </header>

        {canEdit ? (
          <>
            <div className="tabs-container" style={{ marginTop: "16px" }}>
              <button
                onClick={() => setActiveTab("overview")}
                className={`tab-btn ${activeTab === "overview" ? "active" : ""}`}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="9" />
                  <rect x="14" y="3" width="7" height="5" />
                  <rect x="14" y="12" width="7" height="9" />
                  <rect x="3" y="16" width="7" height="5" />
                </svg>
                Overview
              </button>
              <button
                onClick={() => setActiveTab("assistant")}
                className={`tab-btn ${activeTab === "assistant" ? "active" : ""}`}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                Assistant
              </button>
            </div>

            {activeTab === "overview" && (
              <>
                <Highlight />
                <MeetingAgenda />
                <Notes />
              </>
            )}

            {activeTab === "assistant" && (
              <>
                <LiveAssist
                  started={liveAssist.started}
                  startError={liveAssist.startError}
                  canStart={liveAssist.canStart}
                  visibleCards={liveAssist.visibleCards}
                  closeCard={liveAssist.closeCard}
                />
                <TalkingRatio />
                <CueCards />
              </>
            )}
          </>
        ) : (
          <div className="locked-container">
            <div className="locked-icon-wrapper">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h2 className="locked-title">Access Restricted</h2>
            <p className="locked-desc">
              Only the meeting organizer can use Salfri highlights, agenda, and
              notes in this tab.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
